/* Phiếu báo giá chính thức — Tư vấn cơ sở lập, bác sĩ khám xác định và ký, Trưởng phòng duyệt ưu đãi vượt quyền; so với báo giá online (R33) */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, InputNumber, Popconfirm, Segmented, Select, Table, Tag, Tooltip, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { CheckCircle2, Circle, CreditCard, Plus, Printer, Send, Trash2 } from 'lucide-react';
import useAuthStore from '../../store/authStore';
import { MANGS } from '../Telesales/lead/logic';
import {
  CATALOG, NGUONG_TVCS, PROGRAMS, VAT_OPTS, allCodes, calc, dmhm, doctorName, hm, editQuote, lineTotal,
  needApprove, paidOf, quoteIssues, reqLabel, svcById, uid, vnd, vsOnline,
  type ApiSvc, type OfficialQuote, type QLine, type Visit,
} from './logic';
import { useTuVan } from './store';
import { Card, DoctorGate, FlowBar, Hint, LineName, NoVisit, NotStarted, Shell, useMe, useVisit, who } from './shared';
import { useApiServices } from './catalog';
import { cardLine, cardOptions, useCardPackages } from './packages';
import { posBlock, pushQuoteToPos } from './pos';
import { apiLine, draftQuote as draft, fold, onlinePromo } from './quoteDraft';

const Check = ({ ok, children }: { ok: boolean; children: React.ReactNode }) => (
  <li className={`flex items-start gap-2 text-[13px] ${ok ? 'text-gray-700 dark:text-gray-200' : 'text-gray-500'}`}>
    {ok ? <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-green-500" /> : <Circle size={15} className="mt-0.5 shrink-0 text-gray-300" />}
    <span>{children}</span>
  </li>
);

function Body({ v }: { v: Visit }) {
  const navigate = useNavigate();
  const me = useMe();
  const { visits, update } = useTuVan();
  const apiSvcs = useApiServices();
  const pkgs = useCardPackages();
  const { isSuperAdmin, hasPermission } = useAuthStore();
  const canApprove = isSuperAdmin || hasPermission('app_tuvan.approve') || hasPermission('telesales.config');
  const q = v.quote;
  const started = v.status === 'consulting' || v.status === 'done';

  if (!q) {
    return (
      <>
        <NotStarted v={v} />
        <Card>
          <div className="flex flex-col items-center gap-3 py-10 text-center">
            <div className="text-[15px] font-semibold text-gray-700 dark:text-gray-200">Chưa có phiếu báo giá chính thức</div>
            <p className="max-w-lg text-sm text-gray-500">{v.apptSvcs?.length ? 'Phiếu lấy sẵn dịch vụ khách đặt trên lịch hẹn' : v.online ? `Phiếu lấy sẵn dịch vụ theo báo giá online ${v.online.code}` : 'Chọn dịch vụ từ danh mục'}; bác sĩ khám xác định số lượng và ký thì phiếu mới có hiệu lực.</p>
            <Button type="primary" disabled={!started} onClick={() => update(v.id, () => ({ quote: draft(v, me.name, allCodes(visits)) }), { text: 'Lập phiếu báo giá chính thức', by: me.name })}>Lập phiếu</Button>
          </div>
        </Card>
      </>
    );
  }

  const c = calc(q);
  const issues = quoteIssues(q);
  const valid = !issues.length;
  const paid = paidOf(v);
  const cmp = vsOnline(v);
  const locked = !!v.result;
  // Ưu đãi Telesales đã báo khách: phiếu khác đi thì cho lấy lại một chạm
  const online = v.online && (v.online.promo || v.online.discount) ? onlinePromo(v) : undefined;
  const offOnline = !!online && (online.program !== q.program || online.discount !== q.discount);
  const set = (p: Partial<OfficialQuote>, log?: string) => update(v.id, (x) => ({ quote: editQuote(x.quote!, p) }), log ? { text: log, by: me.name } : undefined);
  const setLine = (id: string, p: Partial<QLine>) => set({ lines: q.lines.map((l) => (l.id === id ? { ...l, ...p } : l)) });
  const addSvc = (id: string) => {
    const a = id.startsWith('api-') ? apiSvcs?.find((x) => `api-${x.id}` === id) : undefined;
    if (a) { set({ lines: [...q.lines, apiLine(a)] }); return; }
    const s = svcById(id);
    if (s) set({ lines: [...q.lines, { id: uid(), svcId: s.id, name: s.name, unit: s.unit, qty: 1, price: s.price, core: s.core }] });
  };
  // Bán thẻ liệu trình cùng phiếu với dịch vụ lẻ
  const addCard = (id: string) => {
    const p = pkgs?.find((x) => x.id === id);
    if (p) set({ lines: [...q.lines, cardLine(p)] }, `Thêm thẻ liệu trình ${p.card.code} · ${p.name}`);
  };
  // Danh mục API theo nhóm dịch vụ; chưa tải được thì bảng giá mẫu theo mảng
  const svcOptions = apiSvcs?.length
    ? Object.entries(apiSvcs.reduce<Record<string, ApiSvc[]>>((m, x) => { (m[x.cat || 'Khác'] ??= []).push(x); return m; }, {}))
      .map(([cat, list]) => ({ label: cat, options: list.map((x) => ({ value: `api-${x.id}`, label: `${x.name} · ${vnd(x.price)}` })) }))
    : [v.mang, ...MANGS.filter((m) => m !== v.mang)].map((m) => ({ label: m, options: CATALOG[m].map((s) => ({ value: s.id, label: `${s.name} · ${vnd(s.price)}/${s.unit}` })) }));
  const askApprove = () => update(v.id, (x) => ({ quote: { ...x.quote!, approval: { status: 'pending', pct: q.discount, at: Date.now() } } }), { text: `Xin Trưởng phòng duyệt giảm ${q.discount}%`, by: me.name });
  const decide = (ok: boolean) => update(v.id, (x) => ({ quote: { ...x.quote!, approval: { ...x.quote!.approval!, status: ok ? 'approved' : 'rejected', by: me.name, at: Date.now() } } }),
    { text: `Trưởng phòng ${ok ? 'duyệt' : 'từ chối'} giảm ${q.discount}%`, by: me.name });
  const send = () => { update(v.id, (x) => ({ quote: { ...x.quote!, sentAt: Date.now() } }), { text: `Gửi phiếu ${q.code} cho khách`, by: me.name }); message.success('Đã đánh dấu gửi phiếu cho khách.'); };
  const posWhy = posBlock(v);
  // Ghi giỏ lên lịch hẹn trên hệ thống trước; lỗi thì không đánh dấu đã chuyển (Popconfirm chờ promise, hiện đang tải)
  const toPos = async () => {
    const res = await pushQuoteToPos(v);
    if (!res.ok) { message.error(res.error); return; }
    update(v.id, (x) => ({ quote: { ...x.quote!, posAt: Date.now() } }), { text: `Chuyển giỏ ${q.code} sang POS · ${vnd(c.total)}`, by: me.name });
    message.success('Đã ghi giỏ lên lịch hẹn — quầy POS mở lịch hẹn của khách để thu tiền và trừ cọc.');
  };

  const columns: ColumnsType<QLine> = [
    {
      title: 'Dịch vụ / thẻ liệu trình (tên hiển thị cho khách)', key: 'name',
      render: (_, l) => <LineName l={l} />,
    },
    { title: 'ĐVT', dataIndex: 'unit', width: 70 },
    { title: 'SL', key: 'qty', width: 90, render: (_, l) => <InputNumber size="small" min={1} max={99} value={l.qty} disabled={locked} onChange={(x) => setLine(l.id, { qty: x ?? 1 })} className="!w-[70px]" /> },
    { title: 'Giá niêm yết', key: 'price', width: 130, align: 'right', render: (_, l) => vnd(l.price) },
    {
      title: 'Tính tiền', key: 'gift', width: 130,
      render: (_, l) => <Select size="small" value={l.gift ? 'gift' : 'paid'} disabled={locked || !!l.card} onChange={(x) => setLine(l.id, { gift: x === 'gift' })} className="w-full"
        options={[{ value: 'paid', label: 'Tính tiền' }, { value: 'gift', label: 'Tặng kèm' }]} />,
    },
    { title: 'Thành tiền', key: 'total', width: 130, align: 'right', render: (_, l) => <b>{vnd(lineTotal(l))}</b> },
    {
      title: '', key: 'del', width: 44,
      render: (_, l) => <Button size="small" type="text" danger disabled={locked} icon={<Trash2 size={14} />} onClick={() => set({ lines: q.lines.filter((x) => x.id !== l.id) })} />,
    },
  ];

  return (
    <>
      <NotStarted v={v} />
      <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-3">
        <div className="space-y-4 xl:col-span-2">
          <Card title={<span>Phiếu {q.code}</span>} extra={<span className="text-xs text-gray-500">Lập {dmhm(q.at)} · {who(q.by)} · hiệu lực đến <b>{dayjs(q.validUntil).format('DD/MM/YYYY')}</b></span>}>
            {/* Bác sĩ khám và ký ở bước 2 (không thấy giá); ở đây chỉ đọc lại */}
            <div className={`rounded-lg border p-3 ${q.exam.signedAt ? 'border-green-200 bg-green-50/60 dark:border-green-500/30 dark:bg-green-500/10' : 'border-amber-200 bg-amber-50/60 dark:border-amber-500/30 dark:bg-amber-500/10'}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-[13px] font-semibold text-gray-700 dark:text-gray-200">
                  Bác sĩ khám: {q.exam.doctor ? doctorName(q.exam.doctor) : 'chưa có'}
                  {q.exam.level && <span className="font-normal text-gray-500"> · {q.exam.level}</span>}
                  {q.exam.area && <span className="font-normal text-gray-500"> · {q.exam.area}</span>}
                </span>
                {q.exam.signedAt
                  ? <Tag color="green" className="!m-0">Đã ký {dmhm(q.exam.signedAt)}</Tag>
                  : <Button size="small" type="primary" onClick={() => navigate(`/bac-si-kham?v=${v.id}`)}>Mở bước Bác sĩ khám</Button>}
              </div>
              {!!q.exam.reqs?.length && <div className="mt-1.5 flex flex-wrap gap-1">{q.exam.reqs.map((r) => <Tag key={r} color="red" className="!m-0">{reqLabel(r)}</Tag>)}</div>}
              {q.exam.note && <div className="mt-1 text-xs text-gray-600 dark:text-gray-300">Bác sĩ ghi: {q.exam.note}</div>}
              <Hint>{q.exam.signedAt ? 'Thêm, bớt dịch vụ hoặc đổi số lượng thì bác sĩ phải ký lại. Đổi ưu đãi, thuế không cần ký lại.' : 'Phiếu chỉ có hiệu lực khi bác sĩ đã khám và ký ở bước Bác sĩ khám.'}</Hint>
            </div>

            <div className="mt-3">
              <div className="hidden sm:block"><Table rowKey="id" size="small" columns={columns} dataSource={q.lines} pagination={false} scroll={{ x: 760 }} /></div>
              {/* Điện thoại: mỗi dịch vụ một thẻ — đủ số lượng, giá, thành tiền, không phải kéo ngang */}
              <div className="space-y-2 sm:hidden">
                {q.lines.map((l) => (
                  <div key={l.id} className="rounded-lg border border-gray-200 p-2.5 dark:border-gray-700">
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-[13px] leading-snug"><LineName l={l} /></span>
                      {!locked && <Button size="small" type="text" danger icon={<Trash2 size={14} />} onClick={() => set({ lines: q.lines.filter((x) => x.id !== l.id) })} />}
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <InputNumber min={1} max={99} value={l.qty} disabled={locked} onChange={(x) => setLine(l.id, { qty: x ?? 1 })} className="!w-[64px]" />
                      <span className="text-xs text-gray-500">{l.unit} × {vnd(l.price)}</span>
                      <b className="ml-auto text-[13px]">{l.gift ? 'Tặng' : vnd(lineTotal(l))}</b>
                    </div>
                    <Segmented size="small" block disabled={locked || !!l.card} className="mt-2" value={l.gift ? 'gift' : 'paid'} onChange={(x) => setLine(l.id, { gift: x === 'gift' })}
                      options={[{ value: 'paid', label: 'Tính tiền' }, { value: 'gift', label: 'Tặng kèm' }]} />
                  </div>
                ))}
              </div>
              {!locked && (
                <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                  <Select key={q.lines.length} showSearch placeholder={<span className="inline-flex items-center gap-1"><Plus size={14} />Thêm dịch vụ</span>} onChange={addSvc} className="w-full sm:w-[360px]"
                    filterOption={(input, o) => !!o && 'value' in o && fold(String(o.label)).includes(fold(input))}
                    popupClassName="[&_.ant-select-item-option-content]:!whitespace-normal" listHeight={320}
                    loading={!apiSvcs} options={svcOptions} />
                  <Select key={`c${q.lines.length}`} showSearch placeholder={<span className="inline-flex items-center gap-1"><CreditCard size={14} />Thêm thẻ liệu trình</span>} onChange={addCard} className="w-full sm:w-[360px]"
                    filterOption={(input, o) => !!o && 'value' in o && fold(String(o.label)).includes(fold(input))}
                    popupClassName="[&_.ant-select-item-option-content]:!whitespace-normal" listHeight={320}
                    loading={!pkgs} notFoundContent={pkgs ? 'Chưa có gói đang bán' : undefined} options={cardOptions(pkgs ?? [], true)} />
                </div>
              )}
              {!locked && <Hint>Bán dịch vụ lẻ và thẻ liệu trình trên cùng một phiếu. Thẻ tính theo giá gói (không áp giảm % của phiếu); quầy POS phát hành thẻ khi thu tiền.</Hint>}
            </div>
          </Card>

          <Card title="Ưu đãi" extra={<Tag color={needApprove(q) ? 'orange' : 'cyan'} className="!m-0">Trong quyền đến {NGUONG_TVCS}% · trần {c.cap}%</Tag>}>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <label className="col-span-2 text-[13px] text-gray-500 sm:col-span-1">Chương trình
                <Select allowClear placeholder="Không áp dụng" value={q.program} disabled={locked} onChange={(x) => set({ program: x })} className="mt-1 w-full" options={[...PROGRAMS, ...(q.program && !PROGRAMS.includes(q.program) ? [q.program] : [])].map((p) => ({ value: p, label: p }))} />
              </label>
              <label className="text-[13px] text-gray-500">Giảm (%)
                <InputNumber min={0} max={100} value={q.discount} disabled={locked || !c.base} onChange={(x) => set({ discount: x ?? 0 })} className="mt-1 !w-full" />
              </label>
              <label className="text-[13px] text-gray-500 max-sm:col-span-2">Lý do giảm
                <Input placeholder="Bắt buộc khi có giảm" value={q.reason} disabled={locked} onChange={(e) => update(v.id, (x) => ({ quote: { ...x.quote!, reason: e.target.value } }))} className="mt-1" />
              </label>
            </div>
            {online && (
              <div className={`mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg px-3 py-2 text-[13px] ${offOnline ? 'bg-amber-50 text-amber-800 dark:bg-amber-500/10 dark:text-amber-300' : 'bg-[#e8f4f7] text-[#2c6b7f] dark:bg-[#3e91ab]/15 dark:text-[#8cc8da]'}`}>
                <span>
                  Telesales đã báo khách ({v.online!.code}): {[online.program, online.discount ? `giảm ${online.discount}%` : '', v.online!.reason].filter(Boolean).join(' · ')}
                  {offOnline ? ' — phiếu đang khác ưu đãi này.' : ' — phiếu đang giữ nguyên.'}
                </span>
                {offOnline && !locked && <Button size="small" onClick={() => set(online, `Lấy lại ưu đãi theo báo giá online ${v.online!.code}`)}>Lấy theo báo giá online</Button>}
              </div>
            )}
            {needApprove(q) && q.discount <= c.cap && (
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-amber-50 px-3 py-2 text-[13px] text-amber-800 dark:bg-amber-500/10 dark:text-amber-300">
                <span>
                  {!q.approval && `Giảm ${q.discount}% vượt quyền Tư vấn cơ sở — cần Trưởng phòng duyệt.`}
                  {q.approval?.status === 'pending' && `Đã xin duyệt lúc ${dmhm(q.approval.at)} — chờ Trưởng phòng.`}
                  {q.approval?.status === 'approved' && <span className="text-green-700 dark:text-green-400">Trưởng phòng đã duyệt giảm {q.approval.pct}% · {q.approval.by}</span>}
                  {q.approval?.status === 'rejected' && <span className="text-red-600">Trưởng phòng từ chối — sửa mức giảm.</span>}
                </span>
                <span className="flex gap-2">
                  {!q.approval && <Button size="small" onClick={askApprove} disabled={!q.reason?.trim()}>Xin duyệt</Button>}
                  {q.approval?.status === 'pending' && canApprove && <><Button size="small" danger onClick={() => decide(false)}>Từ chối</Button><Button size="small" type="primary" onClick={() => decide(true)}>Duyệt</Button></>}
                </span>
              </div>
            )}
            <Hint>Giảm % chỉ áp lên dịch vụ không phải chủ lực (không áp lên thẻ liệu trình); quà tặng theo chương trình không tính tiền. Chặn khi tổng giảm vượt 30% mỗi dịch vụ, 40% mỗi combo.</Hint>
          </Card>

          <Card title="Tổng tiền và ký nhận">
            <div className="ml-auto w-full max-w-[380px] space-y-1.5 text-[13px]">
              <div className="flex justify-between"><span className="text-gray-500">Tạm tính</span><span>{vnd(c.sub)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Ưu đãi{q.discount ? ` (${q.discount}%)` : ''}</span><span>{c.disc ? `− ${vnd(c.disc)}` : '0 đ'}</span></div>
              <div className="flex items-center justify-between gap-2"><span className="text-gray-500">Thuế GTGT</span>
                <Select size="small" value={q.vat} disabled={locked} onChange={(x) => set({ vat: x })} className="w-[130px]" options={VAT_OPTS} />
              </div>
              <div className="flex justify-between border-t border-gray-200 pt-2 text-[15px] font-bold dark:border-gray-600"><span>Tổng thanh toán</span><span className="text-[#3e91ab]">{vnd(c.total)}</span></div>
              <div className="flex justify-between text-xs"><span className="text-gray-500">Đã cọc (trừ ở POS)</span><span>{vnd(paid)}</span></div>
              <div className="flex justify-between text-xs font-semibold"><span className="text-gray-500">Còn thu tại POS</span><span>{vnd(Math.max(0, c.total - paid))}</span></div>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2 sm:gap-3">
              <div className="rounded-lg border border-dashed border-gray-300 p-2.5 text-center text-xs dark:border-gray-600"><div className="text-gray-500">Khách hàng</div><div className="mt-1 font-medium">{q.sentAt ? `Đã nhận phiếu ${dmhm(q.sentAt)}` : 'Ký khi nhận phiếu'}</div></div>
              <div className="rounded-lg border border-[#3e91ab]/50 bg-[#3e91ab]/5 p-2.5 text-center text-xs"><div className="text-gray-500">Tư vấn viên</div><div className="mt-1 font-semibold text-[#3e91ab]">{who(v.consultant)}</div></div>
              <div className={`rounded-lg border p-2.5 text-center text-xs ${q.exam.signedAt ? 'border-green-300 bg-green-50 dark:border-green-500/40 dark:bg-green-500/10' : 'border-dashed border-gray-300 dark:border-gray-600'}`}>
                <div className="text-gray-500">Bác sĩ khám</div>
                {q.exam.signedAt && q.exam.sign && <img src={q.exam.sign} alt={`Chữ ký ${doctorName(q.exam.doctor)}`} className="mx-auto mt-1 h-10 max-w-full object-contain" />}
                <div className="mt-1 font-medium">{q.exam.signedAt ? doctorName(q.exam.doctor) : 'Chưa ký'}</div>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2 border-t border-gray-100 pt-3 sm:flex sm:flex-wrap sm:items-center dark:border-gray-700">
              <Button icon={<Printer size={14} />} disabled={!valid} onClick={send}>In / gửi khách</Button>
              <Popconfirm title={`Chuyển giỏ ${vnd(c.total)} sang POS?`} description="Ghi dịch vụ của phiếu lên lịch hẹn; quầy POS thu tiền và trừ cọc đã nhận." okText="Chuyển" cancelText="Huỷ" onConfirm={toPos} disabled={!valid || !!q.posAt || !!posWhy}>
                <Tooltip title={q.posAt ? undefined : posWhy}>
                  <span className="inline-flex w-full sm:w-auto"><Button className="w-full" icon={<Send size={14} />} disabled={!valid || !!q.posAt || !!posWhy}>{q.posAt ? `Đã chuyển POS ${hm(q.posAt)}` : 'Chuyển POS'}</Button></span>
                </Tooltip>
              </Popconfirm>
              <span className="hidden flex-1 sm:block" />
              <Button onClick={() => navigate(`/ke-hoach-thanh-toan?v=${v.id}`)} disabled={!valid}>Kế hoạch thanh toán</Button>
              <Button type="primary" className="col-span-2 sm:col-span-1" onClick={() => navigate(`/ket-qua-tu-van?v=${v.id}`)}>Ghi kết quả</Button>
            </div>
            {issues.length > 0 && <Hint tone="red">Chưa có hiệu lực: {issues.join(' · ')}</Hint>}
            <Hint>Phiếu in tên bác sĩ thực hiện, không in hạng bác sĩ. Phiếu không phải hợp đồng, không cam kết kết quả.</Hint>
          </Card>
        </div>

        <div className="space-y-4 xl:sticky xl:top-[72px]">
          <Card title="So với báo giá online">
            {cmp ? (
              <>
                <div className="grid grid-cols-2 gap-2 text-center">
                  <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-700/40"><div className="text-xs text-gray-500">Online {v.online!.code}</div><div className="mt-1 font-bold">{vnd(cmp.online)}</div><div className="text-[11px] text-gray-400">giá từ</div></div>
                  <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-700/40"><div className="text-xs text-gray-500">Chính thức</div><div className="mt-1 font-bold text-[#3e91ab]">{vnd(cmp.official)}</div><div className="text-[11px] text-gray-400">sau ưu đãi, chưa thuế{cmp.noCards ? ', chưa tính thẻ liệu trình bán thêm' : ''}</div></div>
                </div>
                <div className="mt-3 text-[13px]">Chênh lệch: <b className={cmp.diff > 0 ? 'text-amber-600' : 'text-green-600'}>{cmp.diff > 0 ? '+' : ''}{vnd(cmp.diff)} ({cmp.diff > 0 ? '+' : ''}{cmp.pct}%)</b></div>
                <Hint>(giá chốt − giá online) / giá online. Chỉ số chênh giá tổng hợp ở Báo cáo CCO.</Hint>
              </>
            ) : <Hint>{v.online ? 'Báo giá online chưa có giá để so.' : 'Khách không có báo giá online — không so chênh lệch.'}</Hint>}
          </Card>
          <Card title="Điều kiện hiệu lực">
            <ul className="space-y-1.5">
              <Check ok={q.lines.length > 0}>Có dịch vụ, đủ số lượng và giá</Check>
              <Check ok={!(q.discount > c.cap) && !(q.discount > 0 && !q.reason?.trim())}>Ưu đãi trong trần, có lý do</Check>
              {needApprove(q) && <Check ok={q.approval?.status === 'approved'}>Trưởng phòng duyệt ưu đãi</Check>}
              <Check ok={!!q.exam.signedAt}>Bác sĩ khám ký xác nhận</Check>
              <Check ok={!!q.sentAt}>Gửi phiếu cho khách</Check>
              <Check ok={!!q.posAt}>Chuyển giỏ sang POS</Check>
            </ul>
          </Card>
        </div>
      </div>
    </>
  );
}

export default function OfficialQuotePage() {
  const { v } = useVisit();
  return (
    <Shell title="Phiếu báo giá chính thức" sub="Theo dịch vụ bác sĩ đã khám và ký · tư vấn viên chốt giá, ưu đãi · Trưởng phòng duyệt ưu đãi vượt quyền">
      <FlowBar v={v} step="quote" />
      {v ? <DoctorGate v={v}><Body key={v.id} v={v} /></DoctorGate> : <NoVisit />}
    </Shell>
  );
}
